import React, { createContext, useCallback, useState, useContext } from "react";
import Swal from "sweetalert2";

import { useNavigate } from "react-router-dom";
import {
  ref,
  push as firebasePush,
  set,
  get,
  child,
  remove,
} from "firebase/database";
import { database } from "../config/firebase";
import { FormProps } from "../pages/Sinalario/AddVerbete";

interface SinalarioContextData {
  Verbetes: DataTypeVerbete[];
  loadingVerbetes: boolean;
  createVerbete(VerbeteData: FormProps): Promise<void>;
  editVerbete(VerbetesData: DataTypeVerbete): Promise<void>;
  getVerbete(): Promise<DataTypeVerbete[]>;
  deleteVerbete(uid: string): Promise<void>;
}

export interface DataTypeVerbete {
  uid: string;
  descricao: string;
  image?: string;
  letra: string;
  palavra: string;
  video?: string;
  categoria?: string;
}
const VerbeteContext = createContext<SinalarioContextData>(
  {} as SinalarioContextData
);

const SinalarioProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const [VerbetesData] = useState([] as DataTypeVerbete[]);
  const [loadingVerbetes, setLoadingVerbetes] = useState(false);

  const deleteVerbete = useCallback(async (uid: string) => {
    await remove(child(ref(database), `sinalario/${uid}`));
  }, []);

  const getVerbete = useCallback(async () => {
    try {
      const resposta = await get(child(ref(database), "sinalario"));
      console.log(resposta);

      const VerbeteList = [] as DataTypeVerbete[];
      resposta.forEach((current) => {
        if (current.val()) {
          VerbeteList.push(current.val());
        }
      });
      return VerbeteList;
    } catch (error) {
      throw new Error("Houve um erro Inesperado");
    }
  }, []);

  const editVerbete = useCallback(
    async ({
      uid,
      descricao,
      image,
      letra,
      palavra,
      video,
      categoria,
    }: DataTypeVerbete) => {
      setLoadingVerbetes(true);
      console.log(uid, descricao, image, letra, palavra, video);
      Swal.fire({
        title: "Cadastrando...",
        showConfirmButton: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: async () => {
          Swal.showLoading();
          await set(child(ref(database), `sinalario/${uid}`), {
            uid,
            descricao,
            image,
            letra,
            palavra,
            video: video || "",
            categoria: categoria || "",
          })
            .then(() => {
              Swal.close();
              Swal.fire({
                icon: "success",
                title: "Edição de Verbete",
                text: "Alterações salvas com sucesso!",
                showConfirmButton: false,
                timer: 1800,
              }).then(() => navigate("/sinalario/list"));
            })
            .catch((err) => {
              Swal.fire({
                icon: "error",
                title: "Oops...",
                text: `Aconteceu algum erro inesperado.\nEntre em contato com o suporte técnico.`,
              });
              console.log(err);
              // removed throw to prevent crashing outer scope
            })
            .finally(() => setLoadingVerbetes(false));
        },
      });
    },
    [navigate]
  );

  const createVerbete = useCallback(
    async ({
      descricao,
      image,
      letra,
      palavra,
      video,
      categoria,
    }: FormProps) => {
      setLoadingVerbetes(true);
      console.log(descricao, image, letra, palavra, video);
      Swal.fire({
        title: "Cadastrando...",
        showConfirmButton: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        didOpen: async () => {
          Swal.showLoading();

          const verbeteRef = firebasePush(child(ref(database), "sinalario"));
          const uid = verbeteRef.key;
          await set(verbeteRef, {
            uid,
            descricao,
            image,
            letra,
            palavra,
            video: video || "",
            categoria: categoria || "",
          })
            .then(() => {
              Swal.close();
              Swal.fire({
                icon: "success",
                title: "Cadastro de Verbete",
                text: "Verbete cadastrado com sucesso!",
                showConfirmButton: false,
                timer: 1800,
              }).then(() => navigate("/sinalario/list"));
            })
            .catch((err) => {
              Swal.fire({
                icon: "error",
                title: "Oops...",
                text: `Aconteceu algum erro inesperado.\nEntre em contato com o suporte técnico.`,
              });
              console.log(err);
              // removed throw to prevent crashing outer scope
            })
            .finally(() => setLoadingVerbetes(false));
        },
      });
    },
    [navigate]
  );
  const providerValue = React.useMemo(
    () => ({
      getVerbete,
      editVerbete,
      deleteVerbete,
      createVerbete,
      Verbetes: VerbetesData,
      loadingVerbetes,
    }),
    [
      getVerbete,
      editVerbete,
      deleteVerbete,
      createVerbete,
      VerbetesData,
      loadingVerbetes,
    ]
  );

  return (
    <VerbeteContext.Provider value={providerValue}>
      {children}
    </VerbeteContext.Provider>
  );
};

function useSinalario(): SinalarioContextData {
  const context = useContext(VerbeteContext);

  if (!context) {
    throw new Error("useSinalario must be used within an AuthProvider");
  }

  return context;
}

export { SinalarioProvider, useSinalario };
